import { Modal } from "antd";
import { Controller, FormProvider, useForm } from "react-hook-form";
import CustomSelect from "../../Common/CustomSelect";
import {
    useExportResponsesMutation,
} from "../../../store/api/lumi/projects";
import {
    useGetSurveysQuery, useGetTasksByProjectIdQuery,
    useUpdateSurveyByIdMutation,
} from "../../../store/api/formbricks/survey";
import { useEffect, useMemo, useState } from "react";
import CreateTokenModal from "./CreateTokenModal";

const ImportSurveysModal = ({ onClose, isOpen, projectId }) => {
    const { data: tasksResponse } = useGetTasksByProjectIdQuery(projectId, {
        skip: !projectId,
    });
    const tasks = tasksResponse?.task_list;

    const { data: surveys } = useGetSurveysQuery();

    const [updateSurvey] = useUpdateSurveyByIdMutation();
    const [createExportTask] = useExportResponsesMutation();
    const longLivedToken = localStorage.getItem("long_lived_token");

    const [isCreateTokenModalOpen, setIsCreateTokenModalOpen] = useState(false);

    const closeCreateTokenModalHandler = () => {
        setIsCreateTokenModalOpen(false);
    };

    const selectedSurveys = () =>
        surveys?.filter((survey) =>
            survey.projects.some((project) =>
                tasks?.map((t) => t.project_id).includes(project)
            )
        );

    const defaultValues = useMemo(
        () => ({
            surveys: selectedSurveys()?.map((survey) => survey.id) || [],
        }),
        [selectedSurveys()?.length]
    );

    const closeModalHandler = () => {
        onClose();
        reset(defaultValues);
    };

    const updateAllSurveys = async (newSurveys) => {
        const initialSurveysIds = selectedSurveys()?.map((survey) => survey.id);
        const removedSurveysIds = initialSurveysIds.filter(
            (initialId) => !newSurveys.includes(initialId)
        );

        const findSurveysWithEntityByIds = (ids) => {
            return surveys
                .filter((survey) => ids.includes(survey.id))
                .map((survey) => ({
                    id: survey.id,
                    projects: survey.projects,
                }));
        };

        const updatingPromises = (surveys, action) => {
            return surveys.map((survey) => {
                return updateSurvey({
                    id: survey.id,
                    body: {
                        projects:
                            action === "remove"
                                ? survey.projects.filter(
                                      (project) => project !== projectId
                                  )
                                : [...survey.projects, projectId],
                    },
                });
            });
        };

        const removedSurveysProjects =
            findSurveysWithEntityByIds(removedSurveysIds);

        const updateRemovedPromises = updatingPromises(
            removedSurveysProjects,
            "remove"
        );
        const updateAddedPromises = updatingPromises(
            findSurveysWithEntityByIds(newSurveys),
            "add"
        );

        const updateResults = await Promise.all([
            ...updateAddedPromises,
            ...updateRemovedPromises,
        ]);
    };

    const submitFormHandler = async (data) => {
        try {
            if (!tasks.length && !longLivedToken)
                setIsCreateTokenModalOpen(true);
            if (!tasks.length && longLivedToken) {
                await updateAllSurveys(data.surveys || []);
                await createExportTask({
                    data: {
                        fetch_type: "export_survey_responses",
                        fetch_config: {
                            survey_id: data.surveys[0],
                        },
                        repeat: true,
                        repeat_interval: "day",
                    },
                    projectId,
                    token: longLivedToken,
                });
                closeModalHandler();
            }
            if (tasks.length) {
                await updateAllSurveys(data.surveys || []);
                closeModalHandler();
            }
        } catch (e) {
            console.log(e);
        }
    };

    const methods = useForm({ defaultValues });

    const {
        control,
        reset,
        handleSubmit,
        formState: { isDirty, isSubmitting },
    } = methods;

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues]);

    return (
        <>
            <CreateTokenModal
                isOpen={isCreateTokenModalOpen}
                onClose={closeCreateTokenModalHandler}
            />
            <Modal
                width="60%"
                style={{ maxHeight: "80%", overflow: "auto", paddingBottom: 0 }}
                open={isOpen}
                onCancel={closeModalHandler}
                okButtonProps={{
                    form: "import-surveys",
                    htmlType: "submit",
                    disabled: !isDirty,
                    loading: isSubmitting,
                }}
            >
                <FormProvider {...methods}>
                    <form
                        id="import-surveys"
                        onSubmit={handleSubmit(submitFormHandler)}
                        style={{
                            padding: "20px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px",
                        }}
                    >
                        <Controller
                            control={control}
                            name="surveys"
                            render={({ field }) => (
                                <CustomSelect
                                    {...field}
                                    style={{ width: "100%" }}
                                    label="Select surveys"
                                    mode="multiple"
                                    options={surveys || []}
                                    valueKey="id"
                                    labelKey="name"
                                />
                            )}
                        />
                    </form>
                </FormProvider>
            </Modal>
        </>
    );
};

export default ImportSurveysModal;
